import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faCircle,
  faChevronDown,
  faChevronRight,
  faInfoCircle,
  faFileAlt,
  faPen
} from "@fortawesome/free-solid-svg-icons";

/*
  CompliancePanel.jsx

  Usage:
    <CompliancePanel
      data={yourSectionsArray}   // optional
      onEditItem={fn}            // optional
      onOpenDocument={fn}        // optional
    />
*/

export default function CompliancePanel(props) {
  var data = props.data;
  var onEditItem = props.onEditItem;
  var onOpenDocument = props.onOpenDocument;

  var defaultData = useMemo(function () {
    return buildDemoComplianceData();
  }, []);

  var sections = data && data.length ? data : defaultData;

  var [expandedMap, setExpandedMap] = useState(function () {
    return buildInitialExpandedMap(sections);
  });

  var [hoveredInfoKey, setHoveredInfoKey] = useState(null);

  useEffect(function () {
    setExpandedMap(buildInitialExpandedMap(sections));
  }, [sections]);

  function buildInitialExpandedMap(sectionList) {
    var map = {};
    var i = 0;
    for (i = 0; i < sectionList.length; i++) {
      map[sectionList[i].id] = true; // default expanded for demo impact
    }
    return map;
  }

  function toggleSection(sectionId) {
    setExpandedMap(function (prev) {
      var next = {};
      var key;
      for (key in prev) {
        next[key] = prev[key];
      }
      next[sectionId] = !prev[sectionId];
      return next;
    });
  }

  function getSectionNonComplianceCount(section) {
    var count = 0;
    var i = 0;
    for (i = 0; i < section.items.length; i++) {
      if (section.items[i].status === "fail") {
        count++;
      }
    }
    return count;
  }

  function getSectionRag(section) {
    // RED: any fail
    // AMBER: no fail but any conditional/expiring
    // GREEN: all pass/na
    var hasFail = false;
    var hasAmber = false;

    var i = 0;
    for (i = 0; i < section.items.length; i++) {
      var s = section.items[i].status;
      if (s === "fail") {
        hasFail = true;
      } else if (s === "conditional" || s === "expiring") {
        hasAmber = true;
      }
    }

    if (hasFail) return "red";
    if (hasAmber) return "amber";
    return "green";
  }

  function getRagDotStyle(rag) {
    var style = {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      display: "inline-block",
      marginRight: "8px",
      border: "1px solid #333"
    };

    if (rag === "red") {
      style.background = "#e74c3c";
    } else if (rag === "amber") {
      style.background = "#f1c40f";
    } else {
      style.background = "#2ecc71";
    }

    return style;
  }

  function getStatusIcon(item) {
    if (item.status === "pass") {
      return <FontAwesomeIcon icon={faCheckCircle} />;
    }
    if (item.status === "fail") {
      return <FontAwesomeIcon icon={faTimesCircle} />;
    }
    // conditional/expiring/na etc. show dot
    return <FontAwesomeIcon icon={faCircle} />;
  }

  function getStatusColor(item) {
    if (item.status === "pass") return "#2ecc71";
    if (item.status === "fail") return "#e74c3c";
    if (item.status === "expiring") return "#f39c12";
    if (item.status === "conditional") return "#f1c40f";
    if (item.status === "na") return "#3498db";
    return "#bbb";
  }

  function handleInfoEnter(sectionId, itemId) {
    setHoveredInfoKey(sectionId + ":" + itemId);
  }

  function handleInfoLeave() {
    setHoveredInfoKey(null);
  }

  function handleEdit(section, item) {
    if (typeof onEditItem === "function") {
      onEditItem(section, item);
    } else {
      // demo fallback
      console.log("Edit compliance item:", section.id, item.id);
    }
  }

  function handleOpenDocument(section, item) {
    if (typeof onOpenDocument === "function") {
      onOpenDocument(section, item);
    } else {
      // demo fallback
      console.log("Open document:", section.id, item.id, item.documentName);
    }
  }

  function renderHeader() {
    return (
      <div style={styles.panelHeader}>
        <div style={styles.panelTitle}>Compliance</div>
        <div style={styles.panelSubtitle}>
          Pass / Fail / Conditional with evidence & comments
        </div>
      </div>
    );
  }

  function renderSection(section) {
    var expanded = expandedMap[section.id] === true;
    var rag = getSectionRag(section);
    var nonCompCount = getSectionNonComplianceCount(section);

    return (
      <div key={section.id} style={styles.sectionWrapper}>
        <div
          style={styles.sectionHeader}
          onClick={function () {
            toggleSection(section.id);
          }}
        >
          <div style={styles.sectionHeaderLeft}>
            <div style={styles.chevron}>
              <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
            </div>

            <div style={styles.sectionIcon}>
              <span style={getRagDotStyle(rag)} />
            </div>

            <div style={styles.sectionTitle}>{section.title}</div>
          </div>

          <div style={styles.sectionHeaderRight}>
            <div style={styles.nonCompBadge}>
              {nonCompCount} Non-Compliant
            </div>
          </div>
        </div>

        {expanded ? renderTable(section) : null}
      </div>
    );
  }

  function renderTable(section) {
    return (
      <div style={styles.tableWrapper}>
        <div style={styles.tableHeaderRow}>
          <div style={styles.colStatus}>Status</div>
          <div style={styles.colName}>Compliance</div>
          <div style={styles.colDate}>Tested On</div>
          <div style={styles.colBy}>Tested By</div>
          <div style={styles.colInfo}>Info</div>
          <div style={styles.colDoc}>Doc</div>
          <div style={styles.colEdit}>Edit</div>
        </div>

        {section.items.map(function (item) {
          return renderRow(section, item);
        })}
      </div>
    );
  }

  function renderRow(section, item) {
    var key = section.id + ":" + item.id;
    var showTooltip = hoveredInfoKey === key;
    var hasDoc = item.documentName && item.documentName.length > 0;

    return (
      <div key={item.id} style={styles.tableRow}>
        <div style={styles.colStatus}>
          <span style={{ color: getStatusColor(item), fontSize: "14px" }}>
            {getStatusIcon(item)}
          </span>
        </div>

        <div style={styles.colName}>
          <div style={styles.itemName}>{item.name}</div>
        </div>

        <div style={styles.colDate}>{item.testedOn ? item.testedOn : "—"}</div>

        <div style={styles.colBy}>{item.testedBy ? item.testedBy : "—"}</div>

        <div style={styles.colInfo}>
          <div
            style={styles.iconButton}
            onMouseEnter={function () {
              handleInfoEnter(section.id, item.id);
            }}
            onMouseLeave={function () {
              handleInfoLeave();
            }}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            {showTooltip ? (
              <div style={styles.tooltip}>
                <div style={styles.tooltipTitle}>Comments</div>
                <div style={styles.tooltipBody}>
                  {item.comments && item.comments.length
                    ? item.comments
                    : "No comments recorded."}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div style={styles.colDoc}>
          {hasDoc ? (
            <div
              style={styles.iconButton}
              onClick={function () {
                handleOpenDocument(section, item);
              }}
              title={item.documentName}
            >
              <FontAwesomeIcon icon={faFileAlt} />
            </div>
          ) : (
            <div style={styles.docMissing}>—</div>
          )}
        </div>

        <div style={styles.colEdit}>
          <div
            style={styles.editButton}
            onClick={function () {
              handleEdit(section, item);
            }}
          >
            <FontAwesomeIcon icon={faPen} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panelOuter}>
      {renderHeader()}
      <div style={styles.sectionsArea}>
        {sections.map(function (section) {
          return renderSection(section);
        })}
      </div>
    </div>
  );
}

/* ---------------------------
   Demo Data
---------------------------- */

function buildDemoComplianceData() {
  return [
    {
      id: "electrical",
      title: "Electrical & Equipment",
      items: [
        makeItem("pat", "PAT Test", "pass", "12 Jun 2025", "J. Smith", "Passed. Sticker applied.", "PAT_Cert_2025.pdf"),
        makeItem("fixed", "Fixed Electrical Inspection", "fail", "03 Mar 2024", "XYZ Electrical", "Overdue. Remedial actions outstanding.", "EICR_Report.pdf"),
        makeItem("ext", "Extension Lead Policy", "conditional", "01 Aug 2025", "System", "Temporary lead approved until desk move completed.", ""),
        makeItem("ups", "UPS / Power Strip Certification", "pass", "19 May 2025", "ABC Compliance", "In date and tagged.", "UPS_Cert.pdf")
      ]
    },
    {
      id: "ergonomics",
      title: "Desk & Ergonomics",
      items: [
        makeItem("dse", "DSE Assessment", "conditional", "22 Jul 2025", "H&S Portal", "Chair arms flagged for adjustment. Awaiting follow-up.", ""),
        makeItem("chair", "Chair Adjustment Training", "fail", "", "", "User not yet signed off. Offer 10-min setup session.", ""),
        makeItem("monitor", "Monitor Height Compliance", "conditional", "18 Sep 2025", "Facilities", "Monitor slightly low; riser recommended.", ""),
        makeItem("sitstand", "Sit–Stand Desk Training", "pass", "10 Feb 2025", "Haworth Rep", "User trained; best practices shared.", "Training_Signoff.pdf")
      ]
    },
    {
      id: "fire",
      title: "Fire & Safety",
      items: [
        makeItem("fra", "Fire Risk Assessment", "pass", "05 Jan 2025", "H&S Lead", "All actions closed.", "FRA_2025.pdf"),
        makeItem("exits", "Fire Exit Clearance", "fail", "02 Dec 2025", "Walkthrough", "Obstruction observed near Exit B. Remove items.", ""),
        makeItem("exting", "Fire Extinguisher Inspection", "pass", "11 Nov 2025", "Contractor", "Serviced and tagged.", "Extinguisher_Service.pdf"),
        makeItem("emerg", "Emergency Lighting Test", "expiring", "01 Feb 2025", "Contractor", "Annual test due soon.", "Emergency_Lighting.pdf")
      ]
    },
    {
      id: "env",
      title: "Environment & Comfort",
      items: [
        makeItem("temp", "Temperature Compliance", "conditional", "15 Dec 2025", "Sensor", "One zone trending high during afternoon peak.", ""),
        makeItem("noise", "Noise Level Assessment", "pass", "21 Oct 2025", "Workplace", "Within acceptable range.", "Noise_Assessment.pdf"),
        makeItem("lux", "Lighting Level (Lux)", "expiring", "20 Jan 2025", "Contractor", "Re-test recommended after layout change.", ""),
        makeItem("co2", "Air Quality (CO₂)", "pass", "14 Dec 2025", "Sensor", "Normal operating range.", "")
      ]
    },
    {
      id: "hygiene",
      title: "Hygiene & Facilities",
      items: [
        makeItem("clean", "Desk Cleaning Schedule", "pass", "01 Dec 2025", "Cleaning Team", "Daily clean confirmed.", ""),
        makeItem("san", "Shared Equipment Sanitisation", "pass", "01 Dec 2025", "Facilities", "Station stocked and maintained.", ""),
        makeItem("waste", "Waste & Recycling Compliance", "pass", "05 Nov 2025", "Facilities", "Bin stations labelled correctly.", "")
      ]
    },
    {
      id: "policy",
      title: "Policy & Process",
      items: [
        makeItem("alloc", "Workspace Allocation Policy", "pass", "30 Sep 2025", "Ops", "Allocation matches plan.", ""),
        makeItem("occup", "Maximum Occupancy Compliance", "conditional", "16 Dec 2025", "System", "Peak hour approaching threshold.", ""),
        makeItem("dda", "Accessibility (DDA) Compliance", "pass", "12 Jun 2025", "Consultant", "Access routes maintained.", "DDA_Report.pdf"),
        makeItem("induct", "Contractor Induction Completed", "pass", "10 Dec 2025", "Security", "All contractors signed in.", "")
      ]
    },
    {
      id: "security",
      title: "Security & IT",
      items: [
        makeItem("cable", "Cable Lock Compliance", "fail", "09 Dec 2025", "Audit", "Laptop not secured at hot desk.", ""),
        makeItem("clear", "Clear Desk Policy", "expiring", "01 Oct 2025", "Audit", "Next audit due.", ""),
        makeItem("privacy", "Screen Privacy Compliance", "pass", "13 Nov 2025", "IT", "Privacy filter installed where required.", "Privacy_Filter_Signoff.pdf")
      ]
    }
  ];
}

function makeItem(id, name, status, testedOn, testedBy, comments, documentName) {
  return {
    id: id,
    name: name,
    status: status, // pass | fail | conditional | expiring | na
    testedOn: testedOn,
    testedBy: testedBy,
    comments: comments,
    documentName: documentName
  };
}

/* ---------------------------
   Styles
---------------------------- */

var styles = {
  panelOuter: {
    width: "100%",
    height: "100%",
    background: "#141414",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    overflow: "hidden",
    fontFamily: "Arial, sans-serif"
  },

  panelHeader: {
    padding: "14px 14px 10px 14px",
    borderBottom: "1px solid #2a2a2a",
    background: "#171717"
  },

  panelTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    color: "#fff"
  },

  panelSubtitle: {
    marginTop: "4px",
    fontSize: "11px",
    color: "#9a9a9a"
  },

  sectionsArea: {
    padding: "12px"
  },

  sectionWrapper: {
    marginBottom: "10px",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    overflow: "hidden",
    background: "#161616"
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 12px",
    cursor: "pointer",
    background: "#1a1a1a"
  },

  sectionHeaderLeft: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },

  chevron: {
    width: "18px",
    color: "#bbb"
  },

  sectionIcon: {
    display: "flex",
    alignItems: "center"
  },

  sectionTitle: {
    color: "#fff",
    fontSize: "12px",
    fontWeight: "bold"
  },

  sectionHeaderRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },

  nonCompBadge: {
    fontSize: "11px",
    color: "#ddd",
    border: "1px solid #2f2f2f",
    background: "#151515",
    padding: "4px 10px",
    borderRadius: "999px"
  },

  tableWrapper: {
    padding: "10px 10px 6px 10px"
  },

  tableHeaderRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px 8px",
    border: "1px solid #242424",
    borderRadius: "8px",
    background: "#121212",
    color: "#9a9a9a",
    fontSize: "10px",
    marginBottom: "8px"
  },

  tableRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 8px",
    borderTop: "1px solid #202020",
    color: "#ddd",
    fontSize: "11px"
  },

  colStatus: { width: "60px", display: "flex", alignItems: "center" },
  colName: { flex: 1, minWidth: "220px" },
  colDate: { width: "110px" },
  colBy: { width: "140px" },
  colInfo: { width: "70px", position: "relative" },
  colDoc: { width: "60px", display: "flex", justifyContent: "center" },
  colEdit: { width: "60px", display: "flex", justifyContent: "flex-end" },

  itemName: {
    color: "#fff",
    fontSize: "12px"
  },

  iconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    border: "1px solid #2a2a2a",
    background: "#151515",
    color: "#bbb",
    cursor: "pointer",
    position: "relative"
  },

  docMissing: {
    color: "#666",
    fontSize: "12px"
  },

  editButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "28px",
    borderRadius: "8px",
    border: "1px solid #2a2a2a",
    background: "#151515",
    color: "#ddd",
    cursor: "pointer"
  },

  tooltip: {
    position: "absolute",
    right: "0px",
    top: "34px",
    width: "260px",
    background: "#0f0f0f",
    border: "1px solid #2a2a2a",
    borderRadius: "10px",
    padding: "10px",
    zIndex: 9999,
    boxShadow: "0px 8px 20px rgba(0,0,0,0.45)"
  },

  tooltipTitle: {
    fontSize: "11px",
    fontWeight: "bold",
    color: "#fff",
    marginBottom: "6px"
  },

  tooltipBody: {
    fontSize: "11px",
    color: "#cfcfcf",
    lineHeight: "1.35"
  }
};
